import { z } from 'zod'
import { colorBlindFilterSchema } from '@/lib/a11y/types'

/**
 * Funkcionalne kategorije potreba koje korisnik bira pri registraciji.
 * Framing je "šta ti olakšava korišćenje", a oznake mapiraju vrste invaliditeta.
 */
export const needsCategorySchema = z.enum([
  'dyslexia', // Disleksija
  'low-vision', // Slabovidost
  'blindness', // Slepilo
  'color-blindness', // Daltonizam
  'autism', // Autizam
  'adhd', // ADHD / teškoće pažnje
  'intellectual', // Intelektualne / kognitivne poteškoće
  'deaf-hoh', // Gluvoća / nagluvost
  'motor', // Motorne smetnje
])
export type NeedsCategory = z.infer<typeof needsCategorySchema>

/**
 * Profil potreba — sirovi izbor korisnika. Čuva se da bismo kasnije mogli
 * ponovo da izvedemo podešavanja ako promenimo mapiranje.
 *
 * Bez uzrasta: kod osobe s intelektualnim teškoćama mentalni i biološki uzrast
 * se razilaze, pa je pitanje osjetljivo, a odgovor ionako nije uticao ni na
 * izvedena podešavanja ni na rangiranje preporuka. Stariji zapisi koji još
 * nose `ageGroup` prolaze kroz `parseNeedsProfile` — zod odbacuje višak ključeva.
 */
export const needsProfileSchema = z.object({
  categories: z.array(needsCategorySchema),
  colorBlindType: colorBlindFilterSchema.optional(),
})
export type NeedsProfile = z.infer<typeof needsProfileSchema>

export const EMPTY_NEEDS_PROFILE: NeedsProfile = {
  categories: [],
}

/** Korisnički-prijateljske oznake za UI (kartice u wizard-u). */
export const needsCategoryLabels: Record<NeedsCategory, string> = {
  dyslexia: 'Disleksija',
  'low-vision': 'Slabovidost',
  blindness: 'Slepilo',
  'color-blindness': 'Daltonizam',
  autism: 'Autizam',
  adhd: 'ADHD / teškoće pažnje',
  intellectual: 'Teškoće u učenju',
  'deaf-hoh': 'Gluvoća ili nagluvost',
  motor: 'Motorne smetnje',
}

/** Sigurno parsiranje sa fallback-om na prazan profil. */
export function parseNeedsProfile(raw: unknown): NeedsProfile {
  if (raw == null) return EMPTY_NEEDS_PROFILE

  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch {
      return EMPTY_NEEDS_PROFILE
    }
  }

  const parsed = needsProfileSchema.safeParse(value)
  return parsed.success ? parsed.data : EMPTY_NEEDS_PROFILE
}
